// ICE COLD — "formation" gallery: cards placed in 3D (row, ring or fanned stack),
// browsed by dragging, swiping or horizontal scrolling. Adapted from the Formation component.
// Each frame eases every card toward its pose for the current mode, so switching mode morphs.

(() => {
  if (customElements.get('ice-formation')) return;

  const SPRING = 0.12;
  const PARALLAX_MAX = 6;
  const IDLE_DRIFT = 0.18;

  const wrap = (v, m) => ((v % m) + m) % m;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

  class IceFormation extends HTMLElement {
    connectedCallback() {
      this.world = this.querySelector('.ice-formation__world');
      this.cards = [...this.querySelectorAll('.ice-formation__card')].map((el) => ({
        el,
        cur: { x: 0, y: 0, z: 0, ry: 0, rz: 0, o: 0 },
      }));
      this.n = this.cards.length;
      if (!this.n) return;

      this.mode = this.dataset.mode || 'flat';
      this.browse = 0;
      this.vel = 0;
      this.press = null;
      this.hovering = false;
      this.seeded = false;
      this.tilt = { x: 0, y: 0, tx: 0, ty: 0 };
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.measure = this.measure.bind(this);
      this.frame = this.frame.bind(this);
      this.measure();
      new ResizeObserver(this.measure).observe(this);

      this.querySelectorAll('.ice-formation__dock button').forEach((btn) =>
        btn.addEventListener('click', () => this.setMode(btn.dataset.mode))
      );

      this.addEventListener('pointerdown', (e) => this.down(e));
      this.addEventListener('pointermove', (e) => this.move(e));
      this.addEventListener('pointerup', (e) => this.up(e));
      this.addEventListener('pointercancel', (e) => this.up(e));
      this.addEventListener('pointerenter', () => (this.hovering = true));
      this.addEventListener('pointerleave', () => {
        this.hovering = false;
        this.tilt.tx = 0;
        this.tilt.ty = 0;
        if (this.press && !this.press.committed) this.press = null;
      });
      // Only horizontal wheel/trackpad gestures browse; vertical scrolling stays with the page.
      this.addEventListener(
        'wheel',
        (e) => {
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
          e.preventDefault();
          this.vel = -e.deltaX * 0.25;
          this.browse -= e.deltaX * 0.6;
        },
        { passive: false }
      );

      new IntersectionObserver(
        (entries) => {
          this.onScreen = entries[0].isIntersecting;
          if (this.onScreen) this.classList.add('is-in');
          // Video cards only play while the gallery is on screen.
          this.querySelectorAll('video').forEach((v) => {
            if (this.onScreen && !this.reduced) v.play().catch(() => {});
            else v.pause();
          });
          this.run();
        },
        { threshold: 0.15 }
      ).observe(this);
    }

    measure() {
      const r = this.getBoundingClientRect();
      if (r.width < 1) return;
      this.W = r.width;
      this.H = r.height;
      const mobile = this.W < 750;
      this.cardW = mobile ? clamp(this.W * 0.5, 150, 260) : clamp(this.W * 0.19, 170, 290);
      this.cardH = this.cardW * (4 / 3);
      this.step = this.cardW * 1.08;
      this.cards.forEach(({ el }) => {
        el.style.width = `${this.cardW}px`;
        el.style.height = `${this.cardH}px`;
        el.style.marginLeft = `${-this.cardW / 2}px`;
        el.style.marginTop = `${-this.cardH / 2}px`;
      });
      if (this.reduced) this.paintStatic();
    }

    setMode(mode) {
      if (!mode || mode === this.mode) return;
      this.mode = mode;
      this.dataset.mode = mode;
      this.querySelectorAll('.ice-formation__dock button').forEach((b) =>
        b.setAttribute('aria-selected', String(b.dataset.mode === mode))
      );
      this.browse = 0;
      this.vel = 0;
      if (this.reduced) this.paintStatic();
    }

    pose(i) {
      const { W, n, step, cardW, browse } = this;
      if (this.mode === 'ring') {
        const R = Math.max(cardW * 1.3, (n * step) / (2 * Math.PI));
        const a = (i / n) * Math.PI * 2 + browse / R;
        const c = Math.cos(a);
        return { x: Math.sin(a) * R, y: 0, z: c * R - R, ry: (a * 180) / Math.PI, rz: 0, o: 0.25 + 0.75 * ((c + 1) / 2) };
      }
      if (this.mode === 'stack') {
        let k = wrap(i + browse / step, n);
        if (k > n / 2) k -= n;
        const ak = Math.abs(k);
        return { x: k * cardW * 0.34, y: ak * 10, z: -ak * 90, ry: -k * 8, rz: k * 5, o: ak > 4.5 ? 0 : 1 - ak * 0.14 };
      }
      const total = n * step;
      let x = wrap(i * step + browse, total);
      if (x > total / 2) x -= total;
      return { x, y: 0, z: -Math.abs(x) * 0.35, ry: clamp((-x / W) * 40, -35, 35), rz: 0, o: clamp(1.5 - Math.abs(x) / (W * 0.55), 0, 1) };
    }

    write(card, p) {
      card.el.style.transform = `translate3d(${p.x}px, ${p.y}px, ${p.z}px) rotateY(${p.ry}deg) rotateZ(${p.rz}deg)`;
      card.el.style.opacity = String(p.o);
      card.el.style.zIndex = String(Math.round(1000 + p.z));
    }

    paintStatic() {
      this.cards.forEach((card, i) => {
        const p = this.pose(i);
        Object.assign(card.cur, p);
        this.write(card, p);
      });
    }

    down(e) {
      if (e.target.closest('.ice-formation__dock') || this.press) return;
      this.press = { id: e.pointerId, x: e.clientX, y: e.clientY, last: e.clientX, committed: false };
    }

    move(e) {
      const r = this.getBoundingClientRect();
      this.tilt.ty = ((e.clientX - r.left) / r.width - 0.5) * PARALLAX_MAX;
      this.tilt.tx = (0.5 - (e.clientY - r.top) / r.height) * PARALLAX_MAX;
      const p = this.press;
      if (!p || p.id !== e.pointerId) return;
      if (!p.committed) {
        const dx = Math.abs(e.clientX - p.x);
        const dy = Math.abs(e.clientY - p.y);
        if (dy > dx && dy > 8) {
          // A vertical swipe scrolls the page.
          this.press = null;
          return;
        }
        if (dx > 8) {
          p.committed = true;
          this.setPointerCapture(p.id);
          this.classList.add('is-dragging');
        }
      }
      if (p.committed) {
        const d = (e.clientX - p.last) * (this.mode === 'stack' ? 1 : 1.3);
        this.browse += d;
        this.vel = d;
      }
      p.last = e.clientX;
    }

    up(e) {
      if (!this.press || this.press.id !== e.pointerId) return;
      if (this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
      this.press = null;
      this.classList.remove('is-dragging');
    }

    run() {
      if (this.reduced) {
        this.paintStatic();
        return;
      }
      if (this.onScreen && !this.raf) this.raf = requestAnimationFrame(this.frame);
    }

    frame() {
      this.raf = 0;
      if (!this.onScreen) return;
      const dragging = this.press && this.press.committed;

      if (!dragging) {
        this.browse += this.vel;
        this.vel *= 0.92;
        if (Math.abs(this.vel) < 0.05) {
          this.vel = 0;
          // Settle on the nearest card, then drift slowly while nobody is touching it.
          const target = Math.round(this.browse / this.step) * this.step;
          if (this.hovering) this.browse += (target - this.browse) * 0.08;
          else this.browse += IDLE_DRIFT * (this.mode === 'stack' ? 0.6 : 1);
        }
      }

      this.tilt.x += (this.tilt.tx - this.tilt.x) * 0.06;
      this.tilt.y += (this.tilt.ty - this.tilt.y) * 0.06;
      this.world.style.transform = `rotateX(${this.tilt.x}deg) rotateY(${this.tilt.y}deg)`;

      this.cards.forEach((card, i) => {
        const t = this.pose(i);
        const c = card.cur;
        // Snap across a wrap seam instead of sweeping through the middle.
        if (!this.seeded || Math.abs(t.x - c.x) > this.cardW * 2.2) Object.assign(c, t);
        else for (const k in t) c[k] += (t[k] - c[k]) * SPRING;
        this.write(card, c);
      });
      this.seeded = true;
      this.raf = requestAnimationFrame(this.frame);
    }
  }

  customElements.define('ice-formation', IceFormation);
})();
